import { ColorPalette } from '../../../models/end-client-reporting-package';
import { chartColors } from '../report-aggregation';

/**
 * A named theme over the rows of a battery: "Pricing & Process", "Trust & Credibility".
 *
 * Not a band, though it looks like one. A band is a run of *consecutive* answers on an ordered
 * scale, where position carries the meaning and the two ends make a net. A category is an
 * assignment, in no order at all: it owns whichever rows belong to it, they need not sit together,
 * and there is nothing to net. The two never meet -- bands group the answers *inside* one question,
 * categories group the questions themselves -- so they are deliberately separate fields with
 * separate shapes rather than one idea stretched over both.
 *
 * Rows are named by their **variable key**, not by the label they are charted under. The label was
 * tried first, on the grounds that it is the join `rank-shares` already uses -- and it is wrong: a
 * head-to-head draws a battery whose rows are all the same question worded once, which is why that
 * module does not print the question at all. Every row resolved to the same string, so putting one
 * row in a category put all of them in it.
 *
 * A block never sees the variable map, so it cannot turn a key into a series by itself. It does not
 * have to: `seriesForBlock` builds one series per entry of `variableKeys`, in order, so the key for
 * a row is the one at its own index. Callers pass the keys of the rows they drew.
 */
export interface RowCategory {
  label: string;
  /** The rows it owns, by their variable key. */
  items: string[];
}

export interface CategoryMark {
  label: string;
  colour: string;
}

export interface CategoryReading {
  /** The colour for a given row's variable key, or undefined where no category claims it. */
  colourOf: (key: string) => string | undefined;
  /** The categories that actually claimed a drawn row, in authored order, for the key. */
  legend: CategoryMark[];
}

/** Nothing claimed, nothing drawn: every caller falls back to its own colours. */
const NONE: CategoryReading = { colourOf: () => undefined, legend: [] };

/**
 * Reads the categories against the rows actually drawn.
 *
 * Colours come from `chartColors` by authored position, the same call the ladder makes for its
 * stages and the banded histogram for its bands, so a deck that groups two modules alike colours
 * them alike. Position rather than drawn order, so a category keeps its colour when a filter empties
 * one of its rows -- a theme that changed colour because somebody filtered would be a different
 * theme as far as a reader is concerned.
 *
 * A category that claims none of the drawn rows is left out of the key, since a key naming a colour
 * that is nowhere on the chart is a question rather than an answer.
 */
export function categoriesOf(
  spec: RowCategory[] | undefined,
  /** The variable keys of the rows actually drawn, in the order they were drawn. */
  drawn: string[],
  palette: ColorPalette,
): CategoryReading {
  const named = (spec ?? []).filter(category => category.items?.length);
  if (!named.length) return NONE;

  const fills = chartColors(palette, Math.max(1, (spec ?? []).length));
  const byRow = new Map<string, string>();
  const present = new Set<string>();
  const rows = new Set(drawn);

  (spec ?? []).forEach((category, index) => {
    category.items?.forEach(item => {
      // First claim wins. A row in two categories is an authoring mistake rather than a state to
      // render, and picking one is the only reading that does not change as the list is edited.
      if (byRow.has(item)) return;
      byRow.set(item, fills[index]);
      if (rows.has(item)) present.add(category.label);
    });
  });

  const legend = (spec ?? [])
    .map((category, index) => ({ label: category.label, colour: fills[index] }))
    .filter(mark => present.has(mark.label) && !!mark.label.trim());

  return { colourOf: (row: string) => byRow.get(row), legend };
}
